import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { clsx } from 'clsx';
import { ArrowLeft, ArrowRight, Eye, Loader2, Plus, Undo2, Umbrella } from 'lucide-react';
import { Button } from '@components/Button';
import { Alert, ConfirmDialog, Modal } from '@components/Dialog';
import { ApiErrorModal } from '@components/ApiErrorModal';
import { DateInputBR } from '@components/DateInputBR';
import { PermissionGuard } from '@components/PermissionGuard';
import { Avatar, DueDateLabel, PriorityBadge } from '@components/activity/ActivityBits';
import { useProfile } from '@contexts/ProfileContext';
import coverageService from '@services/coverageService';
import teamService from '@services/teamService';
import boardService from '@services/boardService';
import type { Activity } from '@/types/activity';
import type { Board } from '@/types/board';
import type { Coverage } from '@/types/planning';
import type { TeamMember } from '@/types/team';
import { formatDateBR, formatDateTimeBR, todayISO } from '@/utils/formatters';

const FIELD_LABELS: Record<string, string> = {
  start_date: 'Início do período',
  end_date: 'Fim do período',
  board_ids: 'Quadros',
  include_no_due_date: 'Incluir sem prazo',
  include_overdue: 'Incluir atrasadas',
};

/**
 * Cobertura de férias/folgas: o gestor repassa as atividades de alguém para quem cobre durante um
 * período (pré-visualiza, escolhe quais e confirma) e pode reverter depois. Ocorrências recorrentes
 * geradas no período já nascem com quem cobre (backend).
 */
export const CoverageView: React.FC = () => {
  const [coverages, setCoverages] = useState<Coverage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const [creating, setCreating] = useState(false);
  const [reverting, setReverting] = useState<Coverage | null>(null);
  const [revertBusy, setRevertBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [showReverted, setShowReverted] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setCoverages(await coverageService.getAll());
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleRevert = async () => {
    if (!reverting) return;
    setRevertBusy(true);
    try {
      const result = await coverageService.revert(reverting.id);
      setNotice(
        `${result.reverted} atividade(s) voltaram para ${reverting.from_name}.` +
          (result.kept > 0 ? ` ${result.kept} ficaram com quem cobriu (concluídas ou já reatribuídas).` : ''),
      );
      setReverting(null);
      load();
    } catch (err) {
      setReverting(null);
      setError(err);
    } finally {
      setRevertBusy(false);
    }
  };

  const visible = coverages.filter((c) => showReverted || !c.reverted_at);
  const today = todayISO();

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-h3 flex items-center gap-2">
          <Umbrella size={20} className="text-slate-500" /> Cobertura de férias e folgas
        </h1>
        <div className="flex items-center gap-3">
          <label className="inline-flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
            <input
              type="checkbox"
              className="checkbox-field"
              checked={showReverted}
              onChange={(e) => setShowReverted(e.target.checked)}
            />
            Mostrar revertidas
          </label>
          <PermissionGuard permission="coverage.manage">
            <Button variant="primary" size="sm" icon={<Plus />} onClick={() => setCreating(true)}>
              Nova cobertura
            </Button>
          </PermissionGuard>
        </div>
      </div>

      {notice && (
        <Alert variant="success" onClose={() => setNotice(null)}>
          {notice}
        </Alert>
      )}

      <div className="table-container bg-white">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="table-header">
              <tr>
                <th>De → Para</th>
                <th>Período</th>
                <th>Motivo</th>
                <th className="text-right">Atividades</th>
                <th>Criada</th>
                <th>Situação</th>
                <th className="w-28"></th>
              </tr>
            </thead>
            <tbody>
              {loading && coverages.length === 0 && (
                <tr>
                  <td colSpan={7} className="table-cell text-center text-slate-400">
                    <Loader2 size={18} className="inline animate-spin mr-2" /> Carregando…
                  </td>
                </tr>
              )}
              {!loading && visible.length === 0 && (
                <tr>
                  <td colSpan={7} className="table-cell text-center py-12 text-slate-500">
                    Nenhuma cobertura registrada.
                  </td>
                </tr>
              )}
              {visible.map((c) => {
                const current = !c.reverted_at && c.start_date <= today && c.end_date >= today;
                return (
                  <tr key={c.id} className={clsx('table-row', c.reverted_at && 'opacity-60')}>
                    <td className="table-cell">
                      <div className="flex items-center gap-2">
                        <Avatar name={c.from_name} />
                        <span className="font-medium text-slate-800">{c.from_name}</span>
                        <ArrowRight size={14} className="text-slate-400" />
                        <Avatar name={c.to_name} />
                        <span className="font-medium text-slate-800">{c.to_name}</span>
                      </div>
                    </td>
                    <td className="table-cell whitespace-nowrap">
                      {formatDateBR(c.start_date)} a {formatDateBR(c.end_date)}
                    </td>
                    <td className="table-cell">{c.reason || <span className="text-slate-300">—</span>}</td>
                    <td className="table-cell text-right tabular-nums">{c.item_count}</td>
                    <td className="table-cell">
                      <p className="whitespace-nowrap">{formatDateTimeBR(c.created_at)}</p>
                      <p className="text-small">{c.created_by}</p>
                    </td>
                    <td className="table-cell">
                      {c.reverted_at ? (
                        <>
                          <span className="badge-neutral">Revertida</span>
                          <p className="text-small mt-1">
                            {formatDateTimeBR(c.reverted_at)}
                            {c.reverted_by ? ` · ${c.reverted_by}` : ''}
                          </p>
                        </>
                      ) : current ? (
                        <span className="badge-success">Em andamento</span>
                      ) : c.start_date > today ? (
                        <span className="badge-warning">Agendada</span>
                      ) : (
                        <span className="badge-neutral">Encerrada</span>
                      )}
                    </td>
                    <td className="table-cell text-right">
                      {!c.reverted_at && (
                        <PermissionGuard permission="coverage.manage">
                          <Button variant="outline" size="sm" icon={<Undo2 />} onClick={() => setReverting(c)}>
                            Reverter
                          </Button>
                        </PermissionGuard>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {creating && (
        <NewCoverageModal
          onClose={() => setCreating(false)}
          onCreated={(c) => {
            setCreating(false);
            setNotice(`Cobertura registrada: ${c.item_count} atividade(s) de ${c.from_name} passaram para ${c.to_name}.`);
            load();
          }}
          onError={setError}
        />
      )}

      <ConfirmDialog
        isOpen={!!reverting}
        onClose={() => setReverting(null)}
        onConfirm={handleRevert}
        title="Reverter cobertura"
        message={`Devolver para ${reverting?.from_name ?? ''} as atividades repassadas a ${reverting?.to_name ?? ''}? As já concluídas ou reatribuídas a outra pessoa ficam como estão.`}
        confirmText="Reverter"
        severity="warning"
        loading={revertBusy}
      />

      <ApiErrorModal error={error} onClose={() => setError(null)} fieldLabels={FIELD_LABELS} />
    </div>
  );
};

// ------------------------------------------------------------------ nova cobertura

interface NewCoverageModalProps {
  onClose: () => void;
  onCreated: (c: Coverage) => void;
  onError: (err: unknown) => void;
}

const NewCoverageModal: React.FC<NewCoverageModalProps> = ({ onClose, onCreated, onError }) => {
  const { isAdmin, assignableMembers } = useProfile();
  const [members, setMembers] = useState<TeamMember[]>(assignableMembers);
  const [boards, setBoards] = useState<Board[]>([]);
  const [fromId, setFromId] = useState('');
  const [toId, setToId] = useState('');
  const [startDate, setStartDate] = useState(todayISO());
  const [endDate, setEndDate] = useState('');
  const [reason, setReason] = useState('');
  const [includeNoDue, setIncludeNoDue] = useState(true);
  const [includeOverdue, setIncludeOverdue] = useState(true);
  const [boardIds, setBoardIds] = useState<Set<number>>(new Set());
  const [step, setStep] = useState<'form' | 'preview'>('form');
  const [preview, setPreview] = useState<Activity[]>([]);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [busy, setBusy] = useState(false);

  // Membros: lista da equipe; gestor só enxerga a si e aos subordinados (o backend exige ser
  // gestor dos dois). Sem acesso à lista, cai no perfil (assignableMembers).
  useEffect(() => {
    teamService
      .getAll()
      .then((all) => {
        const active = all.filter((m) => m.is_active);
        const allowed = new Set(assignableMembers.map((m) => m.id));
        const list = isAdmin ? active : active.filter((m) => allowed.has(m.id));
        setMembers([...(list.length > 0 ? list : assignableMembers)].sort((a, b) => a.name.localeCompare(b.name, 'pt-BR')));
      })
      .catch(() => setMembers(assignableMembers));
    boardService
      .getAll()
      .then(setBoards)
      .catch(() => setBoards([]));
  }, [isAdmin, assignableMembers]);

  const fromName = members.find((m) => String(m.id) === fromId)?.name ?? '';
  const toName = members.find((m) => String(m.id) === toId)?.name ?? '';
  const formValid = !!fromId && !!toId && fromId !== toId && !!startDate && !!endDate && endDate >= startDate;

  const toggleBoard = (id: number) =>
    setBoardIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const runPreview = async () => {
    setBusy(true);
    try {
      const list = await coverageService.preview({
        from_member_id: Number(fromId),
        to_member_id: Number(toId),
        start_date: startDate,
        end_date: endDate,
        include_no_due_date: includeNoDue,
        include_overdue: includeOverdue,
        board_ids: boardIds.size > 0 ? [...boardIds] : undefined,
      });
      setPreview(list);
      setSelected(new Set(list.map((a) => a.id)));
      setStep('preview');
    } catch (err) {
      onError(err);
    } finally {
      setBusy(false);
    }
  };

  const confirm = async () => {
    setBusy(true);
    try {
      const created = await coverageService.create({
        from_member_id: Number(fromId),
        to_member_id: Number(toId),
        start_date: startDate,
        end_date: endDate,
        reason: reason.trim() || null,
        activity_ids: [...selected],
      });
      onCreated(created ?? ({ item_count: selected.size, from_name: fromName, to_name: toName } as Coverage));
    } catch (err) {
      onError(err);
    } finally {
      setBusy(false);
    }
  };

  const allSelected = preview.length > 0 && preview.every((a) => selected.has(a.id));
  const toggleOne = (id: number) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const sortedBoards = useMemo(
    () => boards.filter((b) => !b.is_archived).sort((a, b) => a.name.localeCompare(b.name, 'pt-BR')),
    [boards],
  );

  return (
    <Modal isOpen onClose={onClose} size="xl" closeOnBackdrop={!busy} showCloseButton={!busy}>
      <div className="px-6 py-4 border-b border-slate-200">
        <h2 className="text-h3">Nova cobertura</h2>
        <p className="text-small mt-1">
          {step === 'form'
            ? 'Escolha quem sai, quem cobre e o período; depois confira as atividades que serão repassadas.'
            : `${fromName} → ${toName} · ${formatDateBR(startDate)} a ${formatDateBR(endDate)}`}
        </p>
      </div>

      <div className="px-6 py-4 max-h-[65vh] overflow-y-auto custom-scrollbar">
        {step === 'form' ? (
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-label block mb-1">Quem sai (de)</label>
                <select className="select-field" value={fromId} onChange={(e) => setFromId(e.target.value)}>
                  <option value="">Escolha…</option>
                  {members.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-label block mb-1">Quem cobre (para)</label>
                <select className="select-field" value={toId} onChange={(e) => setToId(e.target.value)}>
                  <option value="">Escolha…</option>
                  {members
                    .filter((m) => String(m.id) !== fromId)
                    .map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.name}
                      </option>
                    ))}
                </select>
              </div>
              <div>
                <label className="text-label block mb-1">Início</label>
                <DateInputBR className="input-field w-full" value={startDate} onChange={setStartDate} />
              </div>
              <div>
                <label className="text-label block mb-1">Fim</label>
                <DateInputBR className="input-field w-full" value={endDate} min={startDate || undefined} onChange={setEndDate} />
              </div>
              <div className="sm:col-span-2">
                <label className="text-label block mb-1">Motivo</label>
                <input
                  className="input-field"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Ex.: férias, folga, licença…"
                  maxLength={200}
                />
              </div>
            </div>

            <div className="flex flex-wrap gap-6">
              <label className="inline-flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                <input type="checkbox" className="checkbox-field" checked={includeOverdue} onChange={(e) => setIncludeOverdue(e.target.checked)} />
                Incluir atrasadas
              </label>
              <label className="inline-flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                <input type="checkbox" className="checkbox-field" checked={includeNoDue} onChange={(e) => setIncludeNoDue(e.target.checked)} />
                Incluir sem prazo
              </label>
            </div>

            {sortedBoards.length > 0 && (
              <div>
                <label className="text-label block mb-1">Quadros (nenhum marcado = todos)</label>
                <div className="flex flex-wrap gap-2">
                  {sortedBoards.map((b) => (
                    <label
                      key={b.id}
                      className={clsx(
                        'inline-flex items-center gap-2 rounded-lg border px-2.5 py-1 text-sm cursor-pointer',
                        boardIds.has(b.id) ? 'border-emerald-300 bg-emerald-50 text-emerald-800' : 'border-slate-200 text-slate-700',
                      )}
                    >
                      <input type="checkbox" className="checkbox-field" checked={boardIds.has(b.id)} onChange={() => toggleBoard(b.id)} />
                      {b.name}
                    </label>
                  ))}
                </div>
              </div>
            )}

            {fromId && toId && fromId === toId && <p className="text-sm text-red-600">Quem sai e quem cobre precisam ser pessoas diferentes.</p>}
            {startDate && endDate && endDate < startDate && <p className="text-sm text-red-600">O fim não pode ser antes do início.</p>}
          </div>
        ) : (
          <div className="space-y-3">
            {preview.length === 0 ? (
              <p className="text-center text-slate-500 py-10">
                Nenhuma atividade de {fromName} se encaixa nesses critérios. Você ainda pode confirmar: as ocorrências
                recorrentes geradas no período já nascem com {toName}.
              </p>
            ) : (
              <div className="table-container">
                <table className="w-full">
                  <thead className="table-header">
                    <tr>
                      <th className="w-10">
                        <input
                          type="checkbox"
                          className="checkbox-field"
                          checked={allSelected}
                          onChange={() => setSelected(allSelected ? new Set() : new Set(preview.map((a) => a.id)))}
                        />
                      </th>
                      <th>Atividade</th>
                      <th>Quadro / coluna</th>
                      <th>Prioridade</th>
                      <th>Prazo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.map((a) => (
                      <tr
                        key={a.id}
                        className={clsx('table-row cursor-pointer', !selected.has(a.id) && 'opacity-50')}
                        onClick={() => toggleOne(a.id)}
                      >
                        <td className="px-6 py-3" onClick={(e) => e.stopPropagation()}>
                          <input type="checkbox" className="checkbox-field" checked={selected.has(a.id)} onChange={() => toggleOne(a.id)} />
                        </td>
                        <td className="px-6 py-3 text-sm font-medium text-slate-800">{a.title}</td>
                        <td className="px-6 py-3 text-sm">
                          <p className="text-slate-700">{a.board_name}</p>
                          <p className="text-small">{a.column_name}</p>
                        </td>
                        <td className="px-6 py-3">
                          <PriorityBadge priority={a.priority} />
                        </td>
                        <td className="px-6 py-3">
                          <DueDateLabel activity={a} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="px-6 py-3 border-t border-slate-200 bg-slate-50 rounded-b-lg flex flex-wrap items-center justify-between gap-3">
        {step === 'preview' ? (
          <>
            <Button variant="outline" size="sm" icon={<ArrowLeft />} onClick={() => setStep('form')} disabled={busy}>
              Voltar
            </Button>
            <div className="flex items-center gap-3">
              <span className="text-sm text-slate-600">
                {selected.size} de {preview.length} selecionada(s)
              </span>
              <Button variant="primary" size="sm" onClick={confirm} loading={busy}>
                Confirmar
              </Button>
            </div>
          </>
        ) : (
          <>
            <Button variant="outline" size="sm" onClick={onClose} disabled={busy}>
              Cancelar
            </Button>
            <Button variant="primary" size="sm" icon={<Eye />} onClick={runPreview} loading={busy} disabled={!formValid}>
              Pré-visualizar
            </Button>
          </>
        )}
      </div>
    </Modal>
  );
};
